<div class="aic-one-console">
    <!-- Top Navbar -->
    <Navbar />

    <div class="page-wrapper">
      <!-- Left Sidebar -->
      <Sidebar />

      <!-- Main surface -->
      <div class="hero-section w-100 soft-card">
        <!-- Welcome / Summary -->
        <div class="card border border-0 border-dark border-opacity-75 rounded-4 shadow-lg mb-4">
          <div class="card-body p-3 p-md-4">
            <div
              class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-md-between gap-3"
            >
              <div>
                <h4 class="fw-semibold mb-1 text-start">
                  Welcome, {{ user.name }}
                </h4>
                <p class="text-muted mb-0">
                  Unified access to your apps, projects and governance with role-aware visibility.
                </p>
              </div>

              <div class="d-flex gap-2 ms-md-3">
                <button class="btn btn-outline-dark rounded-3 px-3 text-xxs btn-sm">
                  <i class="fa fa-sliders pe-2"></i> Customize
                </button>
                <button class="btn upload-button rounded-3 px-3">
                  <i class="fa fa-rocket pe-2"></i> Launch New Module
                </button>
              </div>
            </div>

            <!-- KPI row: 3 x KPI + 1 x Kubiki (same row, equal heights) -->
            <div class="row g-3 mt-3 align-items-stretch">
              <!-- KPI cards -->
              <div
                class="col-12 col-md-6 col-lg-3"
                v-for="k in kpis.slice(0, 3)"
                :key="k.key"
              >
                <div class="card kpi-card shadow-lg h-100 border-0 border-top border-3 border-danger">
                  <div class="card-body py-3">
                    <div class="d-flex align-items-center justify-content-between">
                      <div class="text-muted small">{{ k.title }}</div>
                      <i :class="k.icon + ' text-secondary'"></i>
                    </div>
                    <div class="fw-semibold my-1">{{ k.value }}</div>
                    <div class="text-muted small">{{ k.foot }}</div>
                  </div>
                </div>
              </div>

              <!-- Kubiki quick card (sits to the RIGHT of KPIs) -->
              <div class="col-12 col-md-6 col-lg-3">
                <div class="card kpi-card kubiki shadow h-100 border-0 border-top border-3 border-danger">
                  <div class="card-body py-3 d-flex flex-column">
                    <div class="d-flex align-items-center justify-content-between mb-1">
                      <div class="text-muted small">Kubiki AI</div>
                      <i class="fa-solid fa-robot text-secondary"></i>
                    </div>
                    <p class="text-xs text-muted mb-2">
                      Ask, automate and orchestrate across your apps.
                    </p>

                    <div class="mt-auto float-right gap-2">
                      <button class="btn upload-button btn-sm fw-semibold rounded-3" @click="ai_open = true">
                        <i class="fa fa-question me-2"></i> Ask Kubiki
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <!-- /Kubiki -->
            </div>
          </div>
        </div>

        <!-- My Apps -->
        <div class="d-flex align-items-center mb-2">
          <h2 class="h6 mb-0 me-3">My Apps</h2>
            <div class="d-flex align-items-center gap-2 float-left">
              <button
                class="btn btn-sm btn-outline-secondary rounded-3"
                @click="toggle_collapse"
                :aria-expanded="!is_collapsed"
                aria-controls="myAppsGrid"
              >
                <i :class="is_collapsed ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-up'"></i>
                <span class="ms-1">{{ is_collapsed ? 'Show' : 'Hide' }}</span>
              </button>
          </div>
        </div>

        <!-- 3 × 3 layout (max 9 apps) -->
        <div :id="'myAppsGrid'" v-show="!is_collapsed" class="row g-3">
          <div
            class="col-12 col-sm-6 col-lg-4"
            v-for="app in visible_apps.slice(0, 9)"
            :key="app.key"
          >
            <div class="card app-card h-100 border border-opacity-75 rounded-4 bg-white bg-gradient shadow-lg">
              <div class="card-header app-header rounded-top-4">
                  <div class="d-flex justify-content-between align-items-start">
                    <div class="d-flex align-items-center gap-2">
                      <div class="icon-chip d-inline-flex align-items-center justify-content-center">
                        <img :src="app.icon" :alt="`${app.name} icon`" class="app-icon-img d-block object-fit-contain" />
                      </div>
                      <div class="fw-semibold">{{ app.name }}</div>
                    </div>
                    <span class="badge bg-light text-dark rounded-pill small">
                      {{ app.pill }}
                    </span>
                  </div>
                </div>
              <div class="card-body d-flex flex-column">

                <p class="text-muted small mt-2 mb-3 text-start">{{ app.blurb }}</p>

                <div class="row g-2 align-items-end">
                  <!-- KPIs -->
                  <div class="col">
                    <div class="d-flex flex-wrap gap-4 text-muted xxs">
                      <div
                        class="d-block text-uppercase"
                        v-for="(v, label) in app.kpis"
                        :key="label"
                      >
                        <span class="d-block">{{ label }}:</span>
                        <span class="d-block fw-semibold text-dark">{{ v }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Button on the far right, baseline-aligned with values -->
                  <div class="col-auto ms-auto text-end">
                    <button class="btn upload-button fw-bold" @click="on_open_click(app)">
                      Open
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Activity & Governance -->
        <div class="row g-3 mt-3">
          <div class="col-12 col-lg-4">
            <div class="card border border-0 border-dark border-opacity-75 rounded-4 bg-white bg-gradient shadow-lg h-100">
              <div class="card-header">
                <div class="text-start">Governance &amp; Security</div>
              </div>
              <div class="card-body">
                <div class="d-flex align-items-center justify-content-between small mb-2">
                  <span class="text-dark">Open Policy Checks</span>
                  <span class="badge bg-light text-dark rounded-pill">2</span>
                </div>

                <div class="d-flex align-items-center justify-content-between small mb-2">
                  <span class="text-dark">Pending Role Requests</span>
                  <span class="badge bg-light text-dark rounded-pill">1</span>
                </div>

                <div class="d-flex align-items-center justify-content-between small">
                  <span class="text-dark">Last Audit Sync</span>
                  <span class="text-muted xxs">17 Oct 2025 · 08:42</span>
                </div>

                <button class="btn upload-button ms-auto float-end fw-bold mt-3 underline-none">
                  <router-link class="text-white" :to="{ name: 'AuditTrail' }">
                     Open AIC Trail
                  </router-link>
                </button>
              </div>
            </div>
          </div>
          <div class="col-12 col-lg-8">
            <div class="card border border-0 border-dark border-opacity-75 rounded-4 bg-white bg-gradient shadow-lg h-100">
              <div class="card-header">
                <div class="text-start">Recent Activity</div>
              </div>
              <div class="card-body">

                <div class="d-flex flex-column gap-3">
                  <div
                    class="d-flex align-items-start gap-3"
                    v-for="(row, idx) in recent_from_audits"
                    :key="idx"
                  >
                    <div class="activity-chip d-inline-flex align-items-center justify-content-center">
                      <img src="@/assets/aicone.png" class="img-fluid audit-img" />
                    </div>
                    <div class="min-w-0">
                      <!-- 2-line clamp + safe wrapping -->
                      <div class="text-muted xxs clamp-2 break-anywhere" :title="row.d">
                        {{ row.d }}
                      </div>
                    </div>

                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

        <!-- Spacer -->
        <div class="my-4"></div>
      </div>
    </div>

    <!-- Kubiki AI slide-out -->
    <transition name="slide-left">
      <div v-if="ai_open" class="kubiki-panel position-fixed end-0 d-flex flex-column shadow-lg">
        <div class="d-flex align-items-center justify-content-between p-3 border-bottom">
          <div class="d-flex align-items-center gap-2">
            <i class="fa-solid fa-cpu"></i>
            <span class="fw-semibold">Kubiki AI Assistant</span>
          </div>
        <button class="btn btn-link text-dark" @click="ai_open = false">✕</button>
        </div>

        <div class="p-3">
          <p class="small text-muted mb-2">Ask things like:</p>
          <ul class="small ps-3">
            <li>“Generate a compliance report from Proba for Chris Hani Municipality.”</li>
            <li>“Show KYC workflows pending approval in ComplyOn.”</li>
            <li>“Summarize the last 24h audit events across all apps.”</li>
          </ul>

          <div class="mt-3">
            <input class="form-control rounded-3" placeholder="Type a command…" />
            <div class="d-flex gap-2 mt-2">
              <button class="btn upload-button rounded-3">Run</button>
              <button class="btn upload-button rounded-3">Suggest</button>
            </div>
          </div>
        </div>

        <div class="mt-auto p-3 border-top xxs text-muted">
          You are signed in as <span class="fw-semibold text-dark">{{ user.name }}</span>
      </div>
    </div>
  </transition>
</div>